import "intro.js/introjs.css";
import "./IntroSystem.css";

import introJs from "intro.js";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef } from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { topbarStore } from "shared/common/stores/topbarStore";
import {
  getIntroductionShownLS,
  getWhatsnewShownLS,
  setIntroductionShownLS,
  setWhatsnewShownLS,
  waitForAllElements,
} from "shared/common/utils/general-utils";
import { EditSteps } from "./EditSteps";
import { ViewSteps } from "./ViewSteps";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../contexts/SnackbarProvider";

const currentVersion = process.env.npm_package_version || "0.0.0";
const [major, minor] = currentVersion.split(".");
const majorMinor = `${major}.${minor}`;

export default function IntroSystem({
  role,
}: {
  role: "edit" | "view" | "api";
}) {
  const introductionStep = defaultStore((state) => state.introductionStep);
  const changeIntroductionStep = defaultStore(
    (state) => state.changeIntroductionStep
  );
  const showIntroduction = defaultStore((state) => state.showIntroduction);
  const changeShowIntroduction = defaultStore(
    (state) => state.changeShowIntroduction
  );
  const showWhatsNew = defaultStore((state) => state.showWhatsNew);
  const changeShowWhatsNew = defaultStore((state) => state.changeShowWhatsNew);
  const showAxisscale = defaultStore((state) => state.showAxisscale);
  const changeMapExpand = defaultStore((state) => state.changeMapExpand);
  const showStreetMap = defaultStore((state) => state.showStreetMap);

  const changeTopicPopover = sidebarStore((state) => state.changeTopicPopover);

  const changeShowHelp = topbarStore((state) => state.changeShowHelp);
  const changeAnchorHelp = topbarStore((state) => state.changeAnchorHelp);
  const changeAnchorUser = topbarStore((state) => state.changeAnchorUser);

  const { showMessage } = useSnackbar();

  const introWhatsnewHints = introJs();

  const { introductionSteps, whatsnewSteps, whatsnewHints } =
    role === "edit"
      ? EditSteps(showAxisscale || false)
      : ViewSteps(showAxisscale || false);

  const linrefIntroductionSteps = role === "edit" ? [10] : [8];

  const { t } = useTranslation();

  const filteredIntroductionSteps = useMemo(() => {
    // filter out the linrefsteps when no linref enabled
    if (!showAxisscale) {
      return introductionSteps.filter(
        (_, index) => !linrefIntroductionSteps.includes(index)
      );
    }
    return introductionSteps;
  }, [introductionSteps, showAxisscale]);

  const linrefWhatsnewHints = role === "edit" ? [2] : [];

  const filteredWhatsnewHints = useMemo(() => {
    // filter out the linrefhints when no linref enabled
    if (!showAxisscale) {
      return whatsnewHints.filter(
        (_, index) => !linrefWhatsnewHints.includes(index)
      );
    }
    return whatsnewHints;
  }, [whatsnewHints, showAxisscale]);

  const shouldIntroductionStepsBeShown = () =>
    !getIntroductionShownLS(role, "introduction");

  const shouldWhatsnewStepsBeShown = () =>
    !getWhatsnewShownLS(role, majorMinor, "steps") &&
    (whatsnewSteps.length !== 0 || whatsnewHints.length !== 0);

  const showComponent = (title: string) => {
    const nearbySteps = [
      filteredIntroductionSteps[introductionStep - 1],
      filteredIntroductionSteps[introductionStep],
      filteredIntroductionSteps[introductionStep + 1],
    ];

    return nearbySteps.some((step) => step?.title === title);
  };

  useEffect(() => {
    // Map
    if (showComponent(t("onboarding_edit.title_5"))) {
      changeMapExpand(true);
    } else {
      changeMapExpand(false);
    }
    // Ebenen
    if (showComponent(t("onboarding_edit.title_7"))) {
      changeTopicPopover(true);
    }
    // Help menu
    if (showComponent(t("onboarding_edit.title_18"))) {
      changeTopicPopover(false);
      const el: HTMLElement | null =
        document.getElementById("infra3d-helpButton");
      changeAnchorHelp(el);
      changeShowHelp(true);
    } else {
      changeAnchorHelp(null);
      changeShowHelp(false);
    }
    // User menu
    if (showComponent(t("onboarding_edit.title_20"))) {
      const el: HTMLElement | null = document.getElementById("avatarMenu");
      changeAnchorUser(el);
    } else {
      changeAnchorUser(null);
    }
  }, [introductionStep]);

  const defaultOptionsSteps = {
    nextToDone: true,
    exitOnEsc: true,
    tooltipClass: "infra3d-introductionBox",
    showProgress: true,
    showBullets: false,
    disableInteraction: true,
    nextLabel: t("onboarding_buttons.next"),
    prevLabel: t("onboarding_buttons.prev"),
    doneLabel: t("onboarding_buttons.done"),
  };

  // cleanup old entries, can be remove after the next release
  useEffect(() => {
    const editShowIntroduction = localStorage.getItem("edit-showIntroduction");
    if (editShowIntroduction === "no") {
      setIntroductionShownLS("edit", true);
    }
    const viewShowIntroduction = localStorage.getItem("view-showIntroduction");
    if (viewShowIntroduction === "no") {
      setIntroductionShownLS("view", true);
    }
    localStorage.removeItem("edit-showIntroduction");
    localStorage.removeItem("edit-showIntroductionHints");
    localStorage.removeItem("infra3d_whats_new_edit_0_4_0");
    localStorage.removeItem("view-showIntroduction");
    localStorage.removeItem("view-showIntroductionHints");
    localStorage.removeItem("infra3d_whats_new_view_0_4_0");
  }, []);

  useEffect(() => {
    if (showStreetMap) {
      introWhatsnewHints.removeHints();
      return;
    }
    const cancelWait = waitForAllElements({
      selectors: filteredIntroductionSteps.map((step) => step.element),
      onReady: () => {
        if (shouldIntroductionStepsBeShown()) {
          startIntroductionSteps();
        } else {
          if (shouldWhatsnewStepsBeShown()) {
            startWhatsnewSteps();
          }
          startWhatsnewHints(false);
        }
      },
    });

    return () => {
      cancelWait();
    };
  }, [filteredIntroductionSteps, showStreetMap]);

  useEffect(() => {
    // start via help menu
    if (showIntroduction) {
      startIntroductionSteps();
    }
    if (showWhatsNew) {
      if (whatsnewSteps.length === 0 && whatsnewHints.length === 0) {
        showMessage(t("general.no_news"), "info");
        changeShowWhatsNew(false);
      }
      if (whatsnewSteps.length > 0) {
        startWhatsnewSteps();
      }
      if (whatsnewHints.length > 0) {
        startWhatsnewHints(true);
      }
    }
  }, [showIntroduction, showWhatsNew]);

  const introRunning = useRef(false);

  const startIntroductionSteps = () => {
    if (introRunning.current) return; // Don't start if already running

    introRunning.current = true;

    const intro = introJs();

    intro.setOptions({
      ...defaultOptionsSteps,
      steps: filteredIntroductionSteps,
    });

    intro.onchange(() => {
      const currentStep = intro.currentStep();

      if (currentStep) {
        changeIntroductionStep(currentStep);
      }
    });

    intro.onexit(() => {
      introRunning.current = false;
      setIntroductionShownLS(role, true);
      changeShowIntroduction(false);
      changeIntroductionStep(0);

      if (shouldWhatsnewStepsBeShown()) {
        startWhatsnewSteps();
      }
      startWhatsnewHints(false);
    });

    intro.start();
  };

  const startWhatsnewSteps = () => {
    const intro = introJs();

    intro.setOptions({
      ...defaultOptionsSteps,
      tooltipClass: "infra3d-whatsnewBox",
      showProgress: false,
      steps: whatsnewSteps,
    });

    intro.onexit(() => {
      setWhatsnewShownLS(role, majorMinor, "steps");
      changeShowWhatsNew(false);
    });

    intro.start();
  };

  const startWhatsnewHints = (reset: boolean) => {
    // Clear existing hints container (force re-init)
    const existingHints = document.querySelectorAll(".introjs-hint");
    existingHints.forEach((el) => el.remove());

    introWhatsnewHints.setOptions({
      hints: filteredWhatsnewHints,
      hintButtonLabel: t("onboarding_buttons.got_it") as string,
    });

    introWhatsnewHints.onhintclose((hintIndex) => {
      setWhatsnewShownLS(role, majorMinor, "hints", hintIndex);
    });

    introWhatsnewHints.addHints();

    window.addEventListener("resize", handleResize);

    if (!reset) {
      hideAlreadyShownHints();
    }
  };

  function hideAlreadyShownHints() {
    const shownIndexes = getWhatsnewShownLS(role, majorMinor, "hints");
    if (Array.isArray(shownIndexes)) {
      shownIndexes.forEach((index) => {
        introWhatsnewHints.removeHint(index);
      });
    }
  }

  const handleResize = debounce(() => {
    introWhatsnewHints.removeHints(); // Remove old positioned hints
    introWhatsnewHints.addHints(); // Recalculate positions and add again
    hideAlreadyShownHints(); // Re-hide any already seen hints
  }, 300);

  return null;
}
